import _ from 'lodash'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Control, Controller, Resolver, SubmitHandler, useController, UseFormReturn, useForm, useWatch } from 'react-hook-form'
import { useDebouncedCallback } from 'use-debounce'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { Box, Grid, List, ListItem, ListItemIcon, Stack } from '@mui/material'
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded'

import EventNoteIcon from '@mui/icons-material/EventNote'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import TimelapseIcon from '@mui/icons-material/Timelapse'
import LocationOnIcon from '@mui/icons-material/LocationOn'

import { ScheduleRecurrenceType } from '@/core/models/schedule.model'
import { useLanguage } from '@/hooks/useLanguage'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { adapterLocaleMap } from '@/utils/locale'

import TimePickerInput from '@/components/ui/inputs/TimePickerInput'
import { DatePickerInput } from '@/components/ui/inputs/DatePickerInput'
import TextInputController from '@/components/ui/inputs/TextInputController'
import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import { AutocompleteOption } from '@/components/ui/inputs/AutocompleteInput'
import SpecialitiesAutoComplete, { SpecialityAutocompleteOption } from '@/components/ui/autocomplete/SpecialitiesAutoComplete'
import { useGetSpecialityOptionsQuery, useCreateSpecialityOptionMutation } from '@/core/services/speciality-option'

const schema = yup.object({
  consultationReason: yup.string().required(),
  requiredStaffCount: yup.number().required(),
  location: yup.string().required(),
  duration: yup.number().min(15).required(),
  isRecurring: yup.boolean().required(),
  startTime: yup.number().positive().required(),
  endTime: yup.number().positive().required(),
  recurrenceRule: yup.object({
    daysOfWeek: yup.array(yup.number().required()).required(),
    datesOfMonth: yup.array(yup.number().required()).required(),
    monthsOfYear: yup.array(yup.number().required()).required(),
    recurrenceType: yup.string().oneOf(Object.values(ScheduleRecurrenceType)).required(),
    separationCount: yup.number().min(1).required(),
  }),
  // For non-recurring (one-off) templates the user picks a specific date. Required-when-not-
  // recurring is enforced in the submit handler — a yup `.when()` here breaks
  // yup.InferType -> react-hook-form generic inference.
  date: yup.date().optional(),
})

export type ShiftTemplateFormData = yup.InferType<typeof schema>

type ShiftTemplateFormProps = {
  formId: string
  initialValues?: ShiftTemplateFormData
  onSubmit: SubmitHandler<ShiftTemplateFormData>
  /** @deprecated The speciality field now fetches its own options; kept so callers still compile. */
  specialityOptions?: AutocompleteOption[]
  onSearchSpeciality?: (search: string) => void
}

export function ShiftTemplateForm(props: ShiftTemplateFormProps) {
  const { formId, initialValues, onSubmit } = props

  useLingui()
  const isMobile = useIsMobile()

  // Set default start time to 6 PM (18:00) = 18 * 60 = 1080 minutes
  const defaultStartTime = useMemo(() => 1080, [])
  // Set default end time to 8 PM (20:00) = 20 * 60 = 1200 minutes
  const defaultEndTime = useMemo(() => 1200, [])

  const rhForm = useForm<ShiftTemplateFormData>({
    defaultValues: initialValues ?? {
      consultationReason: '',
      requiredStaffCount: 0,
      location: '',
      duration: 15,
      isRecurring: true,
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      recurrenceRule: {
        daysOfWeek: [],
        datesOfMonth: [],
        monthsOfYear: [],
        recurrenceType: ScheduleRecurrenceType.DAILY,
        separationCount: 1,
      },
    },
    // yup.InferType for the schema (with the optional `date` field) doesn't line up
    // with @hookform/resolvers' inferred resolver type, so type the resolver explicitly.
    resolver: yupResolver(schema) as Resolver<ShiftTemplateFormData>,
  })

  // Watch form values for debugging
  const startTime = useWatch({ control: rhForm.control, name: 'startTime' })
  const endTime = useWatch({ control: rhForm.control, name: 'endTime' })

  useEffect(() => {
    if (!open) rhForm.reset()
  }, [open, rhForm])

  return (
    <form id={formId} onSubmit={rhForm.handleSubmit(onSubmit)}>
      <List component="div">
        <ListItem component="div">
          {!isMobile && (
            <ListItemIcon>
              <MonitorHeartIcon />
            </ListItemIcon>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ConsultationReasonField control={rhForm.control} />
            </Grid>
          </Grid>
        </ListItem>
        <ListItem component="div">
          {!isMobile && (
            <ListItemIcon>
              <MonitorHeartIcon />
            </ListItemIcon>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextInputController control={rhForm.control} fullWidth name="requiredStaffCount" type="number" placeholder={t`Required staff count`} />
            </Grid>
          </Grid>
        </ListItem>
        <ListItem component="div">
          {!isMobile && (
            <ListItemIcon>
              <LocationOnIcon />
            </ListItemIcon>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInputController control={rhForm.control} fullWidth name="location" placeholder={t`Location`} />
            </Grid>
          </Grid>
        </ListItem>
        <ListItem component="div">
          <ListItemIcon>
            <EventNoteIcon />
          </ListItemIcon>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <Controller
                  control={rhForm.control}
                  name="startTime"
                  render={({ field }) => (
                    <TimePickerInput
                      fullWidth
                      inputFormat="HH:mm"
                      mask="__:__"
                      onChange={(time) => {
                        if (dayjs.isDayjs(time)) {
                          rhForm.setValue('startTime', time.diff(dayjs().startOf('day'), 'minutes'))
                        }
                      }}
                      value={dayjs().startOf('day').add(field.value, 'minutes')}
                    />
                  )}
                />
                <ArrowRightAltRoundedIcon />
                <Controller
                  control={rhForm.control}
                  name="endTime"
                  render={({ field }) => (
                    <TimePickerInput
                      fullWidth
                      inputFormat="HH:mm"
                      mask="__:__"
                      onChange={(time) => {
                        if (dayjs.isDayjs(time)) {
                          rhForm.setValue('endTime', time.diff(dayjs().startOf('day'), 'minutes'))
                        }
                      }}
                      value={dayjs().startOf('day').add(field.value, 'minutes')}
                    />
                  )}
                />
              </Stack>
            </Grid>
          </Grid>
        </ListItem>
        <ListItem component="div">
          <ListItemIcon>
            <TimelapseIcon />
          </ListItemIcon>
          <Box sx={{ width: 160 }}>
            <DurationField rhForm={rhForm} />
          </Box>
        </ListItem>
        <hr></hr>
        <ListItem component="div" alignItems="flex-start">
          {!isMobile && (
            <ListItemIcon>
              <EventRepeatIcon />
            </ListItemIcon>
          )}
          <Box width="100%">
            <CheckboxInputController control={rhForm.control} label={t`Repeat`} name="isRecurring" />
            <Box mt={2}>
              <ScheduleRecurrenceRule rhForm={rhForm} />
            </Box>
          </Box>
        </ListItem>
      </List>
    </form>
  )
}

// Speciality / consultation-reason picker. Reuses the same SpecialitiesAutoComplete as the
// create-consultation flow (search + inline "Add new speciality" creation), but in single-select
// mode storing the speciality *name* string into the `consultationReason` field.
function ConsultationReasonField({ control }: { control: Control<ShiftTemplateFormData> }) {
  const { field } = useController({ control, name: 'consultationReason' })
  const [searchInput, setSearchInput] = useState<string>()

  const { data: specialities = [] } = useGetSpecialityOptionsQuery(searchInput ? { smartSearch: searchInput } : {}, {
    selectFromResult: ({ data }) => ({ data: data ?? [] }),
  })
  const [createSpecialityOption] = useCreateSpecialityOptionMutation()

  const selectedSpeciality: SpecialityAutocompleteOption | null = field.value ? { _id: '', name: field.value, code: '' } : null

  const handleInputChange = useDebouncedCallback((value: string) => setSearchInput(value), 500)

  const createAndSelect = async (rawName: string) => {
    const name = rawName.trim()
    if (!name) return
    try {
      const created = await createSpecialityOption({ name, code: 'CREATED.' + name.toLowerCase().replace(/ /g, '-') }).unwrap()
      field.onChange(created?.name ?? name)
    } catch {
      // Even if persisting the new speciality fails, keep what the user typed.
      field.onChange(name)
    }
  }

  const handleSpecialityChange = (
    _event: unknown,
    newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null,
    reason: string
  ) => {
    if (Array.isArray(newValue)) return
    if (reason === 'clear' || reason === 'removeOption' || !newValue) {
      field.onChange('')
      return
    }
    if (typeof newValue === 'string') {
      createAndSelect(newValue)
      return
    }
    if (newValue.isNew && newValue.inputValue) {
      createAndSelect(newValue.inputValue)
      return
    }
    field.onChange(newValue.name)
  }

  return (
    <SpecialitiesAutoComplete
      id="shift-template-consultation-reason"
      name="consultationReason"
      label={t`Consultation reason`}
      editable
      canDelete
      multiple={false}
      selectedSpecialities={selectedSpeciality}
      specialities={specialities}
      onInputChange={handleInputChange}
      onSpecialityChange={handleSpecialityChange}
    />
  )
}

function ScheduleRecurrenceRule({ rhForm }: { rhForm: UseFormReturn<ShiftTemplateFormData> }) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const isRecurring = useWatch({ control: rhForm.control, name: 'isRecurring' })

  const recurrenceTypeOptions = useMemo(
    () =>
      Object.values(_.omit(ScheduleRecurrenceType, 'MONTHLY', 'YEARLY')).map((type) => ({
        label: t(`dialog.addSchedule.recurrenceType.${type.toLowerCase()}`),
        value: type,
      })),
    [t]
  )

  if (!isRecurring) {
    // Non-recurring template → pick a single specific date.
    return (
      <Controller
        control={rhForm.control}
        name="date"
        render={({ field }) => (
          <DatePickerInput
            fullWidth
            label={t('page.planning.template.date', { defaultValue: 'Date' })}
            value={field.value ? dayjs(field.value) : null}
            onChange={(value) => field.onChange(value ? value.toDate() : undefined)}
          />
        )}
      />
    )
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="start" spacing={1}>
        <Box fontSize="14px">
          <Trans>Repeat every</Trans>
        </Box>
        <Box sx={{ width: isMobile ? 80 : 150 }}>
          <SeparateCountField rhForm={rhForm} />
        </Box>
        <div>
          <SelectInputController control={rhForm.control} fullWidth name="recurrenceRule.recurrenceType" options={recurrenceTypeOptions} />
        </div>
      </Stack>
      <DaysOfWeekField rhForm={rhForm} />
    </Stack>
  )
}

function DaysOfWeekField({ rhForm }: { rhForm: UseFormReturn<ShiftTemplateFormData> }) {
  const language = useLanguage()
  const { t } = useTranslation()
  const daysOfWeekOptions = useMemo(() => {
    // Localized weekday names via Intl (no dayjs). Avoids two traps: the isoWeek
    // plugin dependency, and dayjs's `.locale(undefined)` getter — which fires when
    // the i18n language (e.g. "en-US") isn't in adapterLocaleMap and crashes with
    // ".format is not a function". Intl falls back to the default locale instead.
    // 2023-01-01 (UTC) is a Sunday, so index n (0=Sun..6=Sat) maps to Jan 1+n,
    // matching the day-of-week values used elsewhere.
    const weekdayFormatter = new Intl.DateTimeFormat(adapterLocaleMap[language], { weekday: 'long', timeZone: 'UTC' })
    return _.range(7).map((n) => ({ label: weekdayFormatter.format(new Date(Date.UTC(2023, 0, 1 + n))), value: n }))
  }, [language])
  const [daysOfWeek, recurrenceType] = useWatch({ control: rhForm.control, name: ['recurrenceRule.daysOfWeek', 'recurrenceRule.recurrenceType'] })

  useEffect(() => {
    if (recurrenceType !== ScheduleRecurrenceType.WEEKLY && daysOfWeek.length !== 0) {
      rhForm.setValue('recurrenceRule.daysOfWeek', [])
    } else if (recurrenceType === ScheduleRecurrenceType.WEEKLY && daysOfWeek.length === 0) {
      rhForm.setValue('recurrenceRule.daysOfWeek', [1])
    }
  }, [daysOfWeek, recurrenceType, rhForm])

  if (recurrenceType !== ScheduleRecurrenceType.WEEKLY) return null

  return (
    <SelectInputController<ShiftTemplateFormData>
      control={rhForm.control}
      enableCheckmark
      label={t('dialog.addSchedule.form.field.daysOfWeek.label')}
      multiple
      name="recurrenceRule.daysOfWeek"
      options={daysOfWeekOptions}
    />
  )
}

function SeparateCountField({ rhForm }: { rhForm: UseFormReturn<ShiftTemplateFormData> }) {
  return (
    <TextInputController
      control={rhForm.control}
      name="recurrenceRule.separationCount"
      type="number"
      inputProps={{ min: 1, step: 1 }}
      error={!!rhForm.formState.errors.recurrenceRule?.separationCount}
    />
  )
}

function DurationField({ rhForm }: { rhForm: UseFormReturn<ShiftTemplateFormData> }) {
  const language = useLanguage()

  // Locale-aware duration labels via Intl (e.g. "15 minutes" / "1 hour") instead of
  // dayjs.duration().humanize(), which needs the duration + relativeTime plugins on a
  // single dayjs instance — fragile under Vite's dep optimizer.
  const options = useMemo(() => {
    const formatDuration = (value: number, unit: 'minute' | 'hour') =>
      new Intl.NumberFormat(adapterLocaleMap[language], { style: 'unit', unit, unitDisplay: 'long' }).format(value)
    return [
      { label: formatDuration(15, 'minute'), value: 15 },
      { label: formatDuration(30, 'minute'), value: 30 },
      { label: formatDuration(45, 'minute'), value: 45 },
      { label: formatDuration(1, 'hour'), value: 60 },
    ]
  }, [language])
  return <SelectInputController<ShiftTemplateFormData> control={rhForm.control} fullWidth name="duration" options={options} />
}
